import React from "react";
import { ShopingGuideCtaCard } from "@aph/ui-lib-humanx";
import { useRouter } from "next/navigation";
import { NEXT_PUBLIC_PHARMACY_MED_IMAGES_BASE_URL } from "@/libs/constants/auth";
import { useAppSelector, useAppDispatch } from "@/redux/hooks";
import { formatPrice } from "@/redux/slice/cart/utils/formatPrice";
import { getSkuQuantity } from "@/redux/slice/cart/utils/getSkuQuantity";
import { cartItemsSelector } from "@/redux/slice/cart/selectors/cartItems";
import { CartItem } from "@/redux/slice/cart/types/CartItem";
import { addCartItem } from "@/redux/slice/cart/utils/addCartItem";
import currentPatientIdSelector from "@/redux/slice/auth/selectors/currentPatientId";
import pharmaAddressDetailsSelector from "@/redux/slice/location/selectors/pharmaAddressDetails";
import { updateCartItem } from "@/redux/slice/cart/utils/updateCartItem";
import { removeCartItem } from "@/redux/slice/cart/utils/removeCartItem";
import { DeviceInfo } from "@/interface/deviceInfo";
import { ProductInfo } from "@/components/ShoppingComponent/types/productInfo";
import ButtonLink from "@/components/ButtonLink";
import { GAAddToCartEvent } from "@/libs/trackingHelpers/GAAddToCartEvent";
import { getRedirectUrl } from "@/components/ShoppingComponent/constant";
import { GAProductClickedEvent } from "@/libs/trackingHelpers/GAProductClickedEvent";
import styles from "./ShoppingWidget3.module.scss";

interface ShoppingWidget3Props {
  userAgent?: DeviceInfo;
  data?: any;
  productInfo?: ProductInfo;
  isPharma?: boolean;
}

const ShoppingWidget3: React.FC<ShoppingWidget3Props> = (props) => {
  const { productInfo, data, isPharma = false } = props;
  const dispatch = useAppDispatch();
  const router = useRouter();
  const cartItems = useAppSelector(cartItemsSelector);
  const pharmaAddressDetails = useAppSelector(pharmaAddressDetailsSelector);
  const currentPatientId = useAppSelector(currentPatientIdSelector);

  const handleAddItemToCart = (product) => {
    if (product?.is_in_stock) {
      const cartItem: CartItem = {
        ...product,
        quantity: Number(product.moq),
      };
      GAAddToCartEvent(cartItem);
      addCartItem({ itemToAdd: cartItem, pharmaAddressDetails, currentPatientId, dispatch });
    }
  };

  const handleQuantityDecrement = (product) => {
    const medicineQtyInCart = getSkuQuantity(product?.sku, cartItems);
    const cartItem: CartItem = {
      ...product,
      quantity: -1,
    };

    if (medicineQtyInCart === Number(product?.moq)) {
      removeCartItem({ sku: product?.sku, currentPatientId, cartItems, dispatch });
    } else {
      updateCartItem({ itemUpdates: cartItem, cartItems, currentPatientId, dispatch });
    }
  };

  const handleQuantityIncrement = (product) => {
    const medicineQtyInCart = getSkuQuantity(product?.sku, cartItems);

    if (medicineQtyInCart < Number(product?.MaxOrderQty || 10)) {
      const cartItem: CartItem = {
        ...product,
        quantity: 1,
      };
      updateCartItem({ itemUpdates: cartItem, cartItems, currentPatientId, dispatch });
    }
  };

  const handleCardClick = (product) => {
    GAProductClickedEvent(product);
    router.push(getRedirectUrl(product, isPharma));
  };

  return (
    <>
      <div className="spacingBetweenSections">
        <div className={`${styles.shopingCard2Root} spacingBetweenSections`}>
          {productInfo?.products?.map((product) => {
            const { name = "", price, special_price, sku = "", thumbnail } = product;

            const medicineQtyInCart = getSkuQuantity(sku, cartItems);

            return (
              <ShopingGuideCtaCard
                key={product?.sku}
                title={name}
                imageUrl={`${NEXT_PUBLIC_PHARMACY_MED_IMAGES_BASE_URL}${thumbnail}`}
                originalPrice={formatPrice(price)}
                actualPrice={formatPrice(Number(special_price))}
                ctaText="Add"
                quantity={medicineQtyInCart}
                addCta={() => {
                  handleAddItemToCart(product);
                }}
                decreaseItemQty={() => {
                  handleQuantityDecrement(product);
                }}
                increaseItemQty={() => {
                  handleQuantityIncrement(product);
                }}
                onClick={() => handleCardClick(product)}
                redirectUrl={getRedirectUrl(product)}
                className={styles.product}
              />
            );
          })}
        </div>
        {data?.ViewAllProductsUrl && (
          <ButtonLink color="tertiarybaby" href={data?.ViewAllProductsUrl} className={styles.btn}>
            Load more products
          </ButtonLink>
        )}
      </div>
    </>
  );
};

export default ShoppingWidget3;
